import { breakpoints } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import {
  NumberField as AriaNumberField,
  NumberFieldProps as AriaNumberFieldProps,
  Group,
  Input,
} from "react-aria-components";
import { useMediaQuery } from "usehooks-ts";

import { Stack } from "../../../foundations/grid-layout/Stack";
import { getTextStyle } from "../../../foundations/typography/styles";
import { _Text } from "../../_assets/_Text";
import { GhostIconButton } from "../../button/GhostIconButton";
import { FormFeedback } from "../_assets/input-wrapper/FormFeedback";
import { FormLabelRAC } from "../_assets/input-wrapper/FormLabelRAC";

export interface NumberFieldProps extends AriaNumberFieldProps {
  label: string;
  info?: string;
  errorMessage?: string;
  testID: string;
  description?: string;
}

export function NumberField(props: NumberFieldProps) {
  const { label, testID, errorMessage, description, ...numberFieldProps } =
    props;

  const isMobile = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-sm"].maxWidth.toString()}px)`
  );

  return (
    <AriaNumberField {...numberFieldProps}>
      <Stack gap="space-1">
        <FormLabelRAC info={props.info} isRequired={props.isRequired}>
          {label}
        </FormLabelRAC>
        <Group className={styles.group}>
          {isMobile ? (
            <>
              <GhostIconButton icon="remove" slot="decrement" />
              <Input
                data-testid={testID}
                className={css(styles.input, styles.inputMobile)}
              />
              <GhostIconButton icon="add" slot="increment" />
            </>
          ) : (
            <>
              <Input
                data-testid={testID}
                className={css(styles.input, styles.inputDesktop)}
              />
              <div>
                <GhostIconButton
                  className={css({ height: "var(--space-5)" })}
                  icon="expand_less"
                  slot="increment"
                />
                <div id="divider" className={styles.divider} />
                <GhostIconButton
                  className={css({ height: "var(--space-5)" })}
                  icon="expand_more"
                  slot="decrement"
                />
              </div>
            </>
          )}
        </Group>
        {description && (
          <_Text style="input/helper" color="text-weak" slot="description">
            {description}
          </_Text>
        )}
        <FormFeedback>{props.errorMessage}</FormFeedback>
      </Stack>
    </AriaNumberField>
  );
}

const styles = {
  divider: css({
    width: "98%",
    height: "var(--space-025)",
    boxShadow: "inset 0px 0px 0px 1px var(--border-input)",
  }),
  group: css({
    display: "flex",
    borderRadius: "var(--border-radius-2)",
    boxShadow: "inset 0px 0px 0px 1px var(--border-input)",

    "&[data-focus-within]": {
      boxShadow: "inset 0px 0px 0px 2px var(--border-input-active)",
      "[id=divider]": {
        boxShadow: "inset 0px 0px 0px 1px var(--border-input-active)",
      },
    },

    "&[data-invalid]": {
      boxShadow: "inset 0 0 0 1px var(--border-danger-input)",
      "[id=divider]": {
        boxShadow: "inset 0 0 0 1px var(--border-danger-input)",
      },
      "&[data-focus-within]": {
        boxShadow: "inset 0px 0px 0px 2px var(--border-danger-input)",
        "[id=divider]": {
          boxShadow: "inset 0px 0px 0px 1px var(--border-danger-input)",
        },
      },
    },

    "&[data-disabled], &[aria-disabled]": {
      boxShadow: "inset 0 0 0 1px var(--border-input-disabled)",
      backgroundColor: "var(--background-input-disabled)",
      "[id=divider]": {
        boxShadow: "inset 0 0 0 1px var(--border-input-disabled)",
      },
    },

    "> button": {
      "&[slot=decrement]": {
        borderStartEndRadius: "0",
        borderEndEndRadius: "0",
      },

      "&[slot=increment]": {
        borderStartStartRadius: "0",
        borderEndStartRadius: "0",
      },
    },
  }),
  input: css(getTextStyle("input/text-field"), {
    width: "100%",
    outline: "none",
    border: "none",
    margin: "0 -1px",
    zIndex: "1",
    textAlign: "left",
    paddingBottom: "var(--space-2)",
    paddingLeft: "var(--space-3)",
    paddingRight: "var(--space-3)",
    paddingTop: "var(--space-2)",
    color: "var(--text-base)",
    boxSizing: "border-box",
    backgroundColor: "var(--background-input)",
    boxShadow: "inset 0 0 0 1px var(--border-input)",
    "::placeholder": { color: "var(--text-placeholder)" },

    "&[data-disabled], &[aria-disabled]": {
      boxShadow: "inset 0 0 0 1px var(--border-input-disabled)",
      backgroundColor: "var(--background-input-disabled)",
      color: "var(--text-weak)",
      "::placeholder": { color: "var(--text-disabled)" },
    },

    "&input&:read-only": {
      color: "var(--text-weak)",
    },
  }),
  inputMobile: css({
    "&[data-focused]": {
      boxShadow:
        "inset 0px -1px 0px 1px var(--border-input-active), inset 0px 1px 0px 1px var(--border-input-active)",
    },

    "&[data-invalid]": css({
      boxShadow: "inset 0 0 0 1px var(--border-danger-input)",
      "&[data-focused]": {
        boxShadow:
          "inset 0px -1px 0px 1px var(--border-danger-input), inset 0px 1px 0px 1px var(--border-danger-input)",
      },
    }),
  }),
  inputDesktop: css({
    borderStartStartRadius: "var(--border-radius-2)",
    borderEndStartRadius: "var(--border-radius-2)",

    "&[data-focused]": {
      boxShadow: "inset 0px 0px 0px 2px var(--border-input-active)",
    },

    "&[data-invalid]": {
      boxShadow: "inset 0 0 0 1px var(--border-danger-input)",
      "&[data-focused]": {
        boxShadow: "inset 0px 0px 0px 2px var(--border-danger-input)",
      },
    },
  }),
};
